"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { FileText, CircleCheck as CheckCircle2, Layers, Clock, ShieldCheck, Target, FileCheck2, Settings, ArrowRight } from 'lucide-react';
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Link } from "@/components/common/Link";
const scopePhases = [{
  phase: "Phase 01",
  title: "Application Assessment & URS Alignment",
  summary: "Detailed review of analytical methods, process requirements, and regulatory standards.",
  duration: "1–2 Weeks",
  items: ["Process parameter and sampling point definition", "Drafting or audit of User Requirement Specifications (URS)", "Regulatory risk analysis against applicable regulatory frameworks", "Technical compatibility review with current facility infrastructure"]
}, {
  phase: "Phase 02",
  title: "Method Feasibility & System Selection",
  summary: "Sample evaluation and technology matching tailored to laboratory throughput.",
  duration: "2–3 Weeks",
  items: ["Spectral & physical property screening on representative batches", "Instrument precision and method suitability assessment", "Vendor-independent hardware & optical interface recommendations", "Data architecture and LIMS/SCADA integration blueprint"]
}, {
  phase: "Phase 03",
  title: "Qualification & Validation Roadmap",
  summary: "Full lifecycle support planning from installation protocols to final operator training.",
  duration: "1–2 Weeks",
  items: ["Customized IQ/OQ/PQ execution plan and test matrices", "Standard Operating Procedure (SOP) drafting guidance", "Preventative maintenance & calibration interval scheduling", "Change control management and continuous verification guidelines"]
}];
const coreDeliverables = [{
  icon: FileCheck2,
  title: "Comprehensive Technical Report",
  description: "In-depth technical synthesis detailing system specifications, feasibility study outcomes, and method suitability data."
}, {
  icon: ShieldCheck,
  title: "Compliance & Risk Matrix",
  description: "Formal assessment documenting data integrity precautions, ALCOA+ compliance alignment, and qualification risks."
}, {
  icon: Settings,
  title: "Integration & Architecture Plan",
  description: "Detailed schematics and communication protocol mapping for inline probes, lab analyzers, and central network data flows."
}, {
  icon: Target,
  title: "Executive Decision Roadmap",
  description: "Clear procurement and implementation timeline with cost estimations, resource allocation, and milestone timelines."
}];
const scopeFAQs = [{
  question: "What is included in the initial site audit?",
  answer: "The initial audit involves an engineer review of current analytical workflows, existing instrument performance, sampling protocols, and facility readiness for process analytical technology (PAT) or benchtop upgrades."
}, {
  question: "Do you supply documentation compliant with regulated GMP environments?",
  answer: "Yes, all consultation protocols, qualification frameworks, and assessment reports are structured directly according to current GAMP 5, FDA, and EMA guidelines for regulated pharmaceutical and chemical manufacturing."
}, {
  question: "Can the scope be adapted for specialized or non-standard matrices?",
  answer: "Absolutely. Our specialists frequently scope custom measurement cells, hazardous area (ATEX) enclosures, and specialized chemometric calibrations for challenging sample matrices."
}];
export default function Deliverables() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0
    },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 16
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.45,
        ease: "easeOut"
      }
    }
  };
  return <section id="consultationdeliverablesscope" data-nav="light" className="relative bg-background py-16 md:py-24 text-foreground">
      <div className="page-container">
        {/* Header Block */}
        <div className="mx-auto max-w-3xl text-center">
          <Badge variant="secondary" className="mb-3 px-3 py-1 text-xs font-semibold uppercase tracking-wider">
            Consultation Framework
          </Badge>
          <h2 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl md:text-5xl">
            Deliverables & Project Scope
          </h2>
          <p className="mt-4 text-base text-muted-foreground sm:text-lg">
            Structured analytical consulting designed to eliminate technical risk, ensure regulatory compliance, and provide actionable engineering roadmaps for regulated laboratories.
          </p>
        </div>

        {/* Core Deliverables Grid */}
        <div className="mt-14">
          <div className="mb-6 flex flex-col items-start justify-between gap-2 sm:flex-row sm:items-end">
            <div>
              <h3 className="text-xl font-semibold tracking-tight text-foreground sm:text-2xl">
                Standard Consultation Deliverables
              </h3>
              <p className="text-sm text-muted-foreground">
                Documented outputs provided at the conclusion of each consulting engagement.
              </p>
            </div>
          </div>

          <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
          once: true,
          amount: 0.2
        }} className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {coreDeliverables.map((item, index) => {
            const IconComp = item.icon;
            return <motion.div key={item.title} data-index={index} variants={itemVariants}>
                  <Card className="h-full border border-border bg-card shadow-sm transition-[border-color,box-shadow] duration-200 hover:border-primary/40 hover:shadow-md">
                    <CardHeader className="gap-3">
                      <div className="flex size-11 items-center justify-center rounded-lg bg-secondary text-primary">
                        <IconComp className="size-5" />
                      </div>
                      <CardTitle className="text-base font-semibold text-foreground">
                        {item.title}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <CardDescription className="text-sm leading-relaxed text-muted-foreground">
                        {item.description}
                      </CardDescription>
                    </CardContent>
                  </Card>
                </motion.div>;
          })}
          </motion.div>
        </div>

        {/* Structured Phased Scope Section */}
        <div className="mt-16 rounded-2xl border border-border bg-secondary/30 p-6 sm:p-8 md:p-10">
          <div className="max-w-2xl">
            <Badge variant="outline" className="mb-2 bg-background text-foreground">
              Lifecycle Execution
            </Badge>
            <h3 className="text-2xl font-bold tracking-tight text-foreground sm:text-3xl">
              Phased Engagement Scope
            </h3>
            <p className="mt-2 text-sm text-muted-foreground sm:text-base">
              A clear step-by-step methodology ensuring every laboratory requirement is defined, tested, and validated.
            </p>
          </div>

          <div className="mt-8 space-y-6">
            {scopePhases.map((phase, index) => <div key={phase.phase} data-index={index} className="rounded-xl border border-border bg-card p-6 shadow-xs transition-[border-color,box-shadow] duration-200 hover:border-primary/30">
                <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                  <div className="space-y-1">
                    <div className="flex items-center gap-2.5">
                      <span className="text-xs font-bold tracking-wider text-primary uppercase">
                        {phase.phase}
                      </span>
                      <span className="text-muted-foreground/40">•</span>
                      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                        <Clock className="size-3.5" />
                        <span>{phase.duration}</span>
                      </div>
                    </div>
                    <h4 className="text-lg font-semibold text-foreground">
                      {phase.title}
                    </h4>
                    <p className="text-sm text-muted-foreground">
                      {phase.summary}
                    </p>
                  </div>
                </div>

                <Separator className="my-4" />

                <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2">
                  {phase.items.map((point, pIndex) => <div key={pIndex} data-index={pIndex} className="flex items-start gap-2.5 text-sm text-foreground/90">
                      <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-primary" />
                      <span>{point}</span>
                    </div>)}
                </div>
              </div>)}
          </div>
        </div>

        {/* Scope Boundaries & FAQ Split */}
        <div className="mt-16 grid grid-cols-1 gap-8 lg:grid-cols-12">
          {/* Left: Out of Scope & In Scope Clarity */}
          <div className="flex flex-col justify-between rounded-xl border border-border bg-card p-6 shadow-xs lg:col-span-5">
            <div className="space-y-4">
              <div className="flex size-10 items-center justify-center rounded-lg bg-secondary text-foreground">
                <Layers className="size-5 text-primary" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-foreground">
                  Scope Boundaries & Assurance
                </h3>
                <p className="mt-1 text-sm text-muted-foreground">
                  We define project parameters up front so teams have transparent expectations before deployment.
                </p>
              </div>

              <div className="space-y-3 pt-2">
                <div className="rounded-lg border border-border bg-background p-3.5">
                  <span className="text-xs font-semibold text-foreground">Included in Scope:</span>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Vendor-neutral technology appraisal, sample suitability protocols, data integrity risk documentation, and custom URS definition.
                  </p>
                </div>
                <div className="rounded-lg border border-border bg-background p-3.5">
                  <span className="text-xs font-semibold text-foreground">Custom Add-on Scope:</span>
                  <p className="mt-1 text-xs text-muted-foreground">
                    On-site physical validation execution (IQ/OQ), bespoke chemometric model development, and multi-site SOP standardization.
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-6 pt-4 border-t border-border">
              <Link to="/ContactPage" className="inline-block w-full">
                <Button className="w-full justify-between" size="lg">
                  <span>Schedule Consultation</span>
                  <ArrowRight className="size-4" />
                </Button>
              </Link>
            </div>
          </div>

          {/* Right: Interactive Scope Clarifications */}
          <div className="rounded-xl border border-border bg-card p-6 shadow-xs lg:col-span-7">
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-foreground">
                Scope & Deliverables FAQ
              </h3>
              <p className="text-sm text-muted-foreground">
                Common questions regarding the consultation engagement process.
              </p>
            </div>

            <Accordion type="single" collapsible defaultValue="item-0" className="w-full">
              {scopeFAQs.map((faq, index) => <AccordionItem key={index} value={`item-${index}`}>
                  <AccordionTrigger className="text-sm font-medium text-foreground hover:no-underline">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm leading-relaxed text-muted-foreground">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>)}
            </Accordion>
          </div>
        </div>
      </div>
    </section>;
}